/**
 * Rechnungs-PDF herunterladen oder im Browser öffnen.
 */

import { API_BASE, ApiError } from "@/lib/apiClient";

export type InvoicePdfMode = "preview" | "download";

function buildPdfUrl(invoiceId: string, withLetterhead = false): string {
  const path = `/api/v1/invoices/${invoiceId}/pdf`;
  const query = withLetterhead ? "?with_letterhead=true" : "";
  if (!API_BASE) return `${path}${query}`;
  return `${API_BASE}${path}${query}`;
}

function buildXrechnungUrl(invoiceId: string): string {
  const path = `/api/v1/invoices/${invoiceId}/xrechnung`;
  if (!API_BASE) return path;
  return `${API_BASE}${path}`;
}

function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match?.[1]?.trim() || fallback;
}

export function canShowInvoicePdf(status: string): boolean {
  return ["draft", "issued", "partially_paid", "paid"].includes(status);
}

async function requestInvoicePdf(
  invoiceId: string,
  withLetterhead: boolean,
): Promise<{ blob: Blob; filename: string | null }> {
  let res: Response;
  try {
    res = await fetch(buildPdfUrl(invoiceId, withLetterhead), { credentials: "include" });
  } catch (err) {
    const message =
      err instanceof Error ? err.message : "PDF konnte nicht geladen werden.";
    throw new ApiError(0, message);
  }

  if (res.status === 401) {
    throw new ApiError(401, "Anmeldung erforderlich.");
  }

  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      try {
        const parsed = (await res.json()) as { detail?: unknown };
        if (typeof parsed.detail === "string") message = parsed.detail;
      } catch {
        /* ignore */
      }
    }
    throw new ApiError(res.status, message);
  }

  return {
    blob: await res.blob(),
    filename: res.headers.get("Content-Disposition"),
  };
}

/** PDF-Bytes für eine eingebettete Vorschau. Entwürfe enthalten das Wasserzeichen ENTWURF. */
export async function loadInvoicePdfBlob(
  invoiceId: string,
  options?: { withLetterhead?: boolean },
): Promise<Blob> {
  const { blob } = await requestInvoicePdf(invoiceId, options?.withLetterhead ?? false);
  return blob;
}

export async function fetchInvoicePdf(
  invoiceId: string,
  invoiceNumber: string,
  mode: InvoicePdfMode,
): Promise<void> {
  const { blob, filename: disposition } = await requestInvoicePdf(invoiceId, false);
  const filename = filenameFromDisposition(disposition, `${invoiceNumber}.pdf`);
  const blobUrl = URL.createObjectURL(blob);

  if (mode === "preview") {
    const tab = window.open(blobUrl, "_blank", "noopener,noreferrer");
    if (!tab) {
      URL.revokeObjectURL(blobUrl);
      throw new ApiError(0, "Pop-up blockiert. Bitte Pop-ups erlauben oder „PDF speichern“ nutzen.");
    }
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    return;
  }

  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}

export async function fetchInvoiceXrechnung(
  invoiceId: string,
  invoiceNumber: string,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(buildXrechnungUrl(invoiceId), { credentials: "include" });
  } catch (err) {
    const message =
      err instanceof Error ? err.message : "X-Rechnung konnte nicht geladen werden.";
    throw new ApiError(0, message);
  }

  if (res.status === 401) {
    throw new ApiError(401, "Anmeldung erforderlich.");
  }

  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      try {
        const parsed = (await res.json()) as { detail?: unknown };
        if (typeof parsed.detail === "string") message = parsed.detail;
      } catch {
        /* ignore */
      }
    }
    throw new ApiError(res.status, message);
  }

  const blob = await res.blob();
  const filename = filenameFromDisposition(
    res.headers.get("Content-Disposition"),
    `${invoiceNumber}-xrechnung.xml`,
  );
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}

function buildDunningPdfUrl(invoiceId: string): string {
  const path = `/api/v1/invoices/${invoiceId}/dunning-pdf`;
  if (!API_BASE) return path;
  return `${API_BASE}${path}`;
}

export async function fetchDunningPdfPreview(invoiceId: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(buildDunningPdfUrl(invoiceId), { credentials: "include" });
  } catch (err) {
    const message =
      err instanceof Error ? err.message : "Mahn-PDF konnte nicht geladen werden.";
    throw new ApiError(0, message);
  }

  if (res.status === 401) {
    throw new ApiError(401, "Anmeldung erforderlich.");
  }

  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      try {
        const parsed = (await res.json()) as { detail?: unknown };
        if (typeof parsed.detail === "string") message = parsed.detail;
      } catch {
        /* ignore */
      }
    }
    throw new ApiError(res.status, message);
  }

  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const tab = window.open(blobUrl, "_blank", "noopener,noreferrer");
  if (!tab) {
    URL.revokeObjectURL(blobUrl);
    throw new ApiError(0, "Pop-up blockiert. Bitte Pop-ups erlauben.");
  }
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}
